import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  AiAgentsService,
  AiConnectionScope,
  AiConversation,
  AiConversationGroup,
  AiMessage,
  AiModelConnection,
} from './ai-agents';

@Injectable({ providedIn: 'root' })
export class AiAgentChatStateService {
  readonly open = signal(false);
  readonly setupRequired = signal(false);
  readonly recentConversations = signal<AiConversation[]>([]);
  readonly pinnedConversations = signal<AiConversation[]>([]);
  readonly groups = signal<AiConversationGroup[]>([]);
  readonly groupConversations = signal<Record<string, AiConversation[]>>({});
  readonly conversations = computed(() => {
    const unique = new Map<string, AiConversation>();
    [...this.pinnedConversations(), ...Object.values(this.groupConversations()).flat(), ...this.recentConversations()]
      .forEach(item => unique.set(item.id, item));
    return [...unique.values()];
  });
  readonly messages = signal<AiMessage[]>([]);
  readonly availableConnections = signal<AiModelConnection[]>([]);
  readonly connectionModels = signal<Record<string, string[]>>({});
  readonly selectedScope = signal<AiConnectionScope>('organisation');
  readonly selectedConnectionId = signal<string | null>(null);
  readonly selectedModel = signal<string | null>(null);
  readonly activeConversationId = signal<string | null>(null);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly loadingModels = signal(false);
  readonly loadingMoreRecent = signal(false);
  readonly hasMoreRecent = signal(false);
  readonly runningConversationIds = signal<Set<string>>(new Set());
  readonly unreadConversationIds = signal<Set<string>>(new Set());
  readonly error = signal('');
  readonly quotaError = signal('');
  readonly activeConversation = computed(() => this.conversations().find(item => item.id === this.activeConversationId()) ?? null);
  readonly scopedConnections = computed(() => this.availableConnections().filter(connection => connection.scope === this.selectedScope()));
  readonly availableModels = computed(() => {
    const connectionId = this.selectedConnectionId();
    return connectionId ? this.connectionModels()[connectionId] ?? [] : [];
  });
  private initialized = false;
  private recentPage = 1;

  constructor(private readonly api: AiAgentsService) {}

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.loading.set(true);
    this.error.set('');
    this.quotaError.set('');
    try {
      const [status, connections] = await Promise.all([
        firstValueFrom(this.api.connectionStatus()),
        firstValueFrom(this.api.availableConnections()),
      ]);
      this.setupRequired.set(!status.connected);
      this.selectedScope.set(status.scope ?? connections[0]?.scope ?? 'organisation');
      this.setConnections(connections);
      await Promise.all([this.refreshNavigation(), this.loadSelectedConnectionModels()]);
    } catch (error) {
      this.handleError(error, 'Could not load AI assistant conversations.');
    } finally {
      this.loading.set(false);
    }
  }

  async refreshForOrganisationChange(): Promise<void> {
    this.initialized = false;
    this.recentPage = 1;
    this.activeConversationId.set(null);
    this.messages.set([]);
    this.recentConversations.set([]);
    this.pinnedConversations.set([]);
    this.groups.set([]);
    this.groupConversations.set({});
    this.availableConnections.set([]);
    this.connectionModels.set({});
    this.selectedConnectionId.set(null);
    this.selectedModel.set(null);
    this.unreadConversationIds.set(new Set());
    this.runningConversationIds.set(new Set());
    this.hasMoreRecent.set(false);
    this.error.set('');
    this.quotaError.set('');
    await this.initialize();
  }

  async refreshConnectionStatus(): Promise<boolean> {
    try {
      const [status, connections] = await Promise.all([firstValueFrom(this.api.connectionStatus()), firstValueFrom(this.api.availableConnections())]);
      this.setupRequired.set(!status.connected);
      this.setConnections(connections);
      await this.loadSelectedConnectionModels();
      return status.connected;
    } catch (error) {
      this.handleError(error, 'Could not check the AI model connection.');
      return false;
    }
  }

  toggle(): void { this.open.update(value => !value); if (this.open()) void this.initialize(); }
  close(): void { this.open.set(false); }

  newConversation(): void {
    this.activeConversationId.set(null);
    this.messages.set([]);
    this.error.set('');
    this.quotaError.set('');
    void this.refreshConnectionStatus();
  }

  async selectConversation(id: string): Promise<void> {
    if (id.startsWith('pending:')) return;
    this.setConversationUnread(id, false);
    this.activeConversationId.set(id);
    this.loading.set(true);
    this.error.set('');
    this.quotaError.set('');
    try { this.messages.set(await firstValueFrom(this.api.listMessages(id))); }
    catch (error) { this.handleError(error, 'Could not load this conversation.'); }
    finally { this.loading.set(false); }
  }

  clearActiveConversation(): void { this.newConversation(); }

  async send(content: string): Promise<boolean> {
    const normalized = content.trim();
    if (!normalized || this.sending()) return false;
    const requestId = crypto.randomUUID();
    const existingConversationId = this.activeConversationId();
    const pendingConversationId = existingConversationId ?? `pending:${requestId}`;
    this.setConversationRunning(pendingConversationId, true);
    if (!existingConversationId) this.addPendingConversation(pendingConversationId, normalized);
    this.messages.update(items => [...items, this.optimisticUserMessage(normalized, requestId, existingConversationId ?? '')]);
    this.sending.set(true);
    this.error.set('');
    this.quotaError.set('');
    try {
      if (!(await this.refreshConnectionStatus())) {
        this.messages.update(items => items.filter(item => item.client_request_id !== requestId));
        this.removePendingConversation(pendingConversationId);
        this.setupRequired.set(true);
        return false;
      }
      let conversationId = existingConversationId;
      if (conversationId) {
        await firstValueFrom(this.api.send(conversationId, normalized, requestId, this.selectedConnectionId() ?? undefined, this.selectedModel() ?? undefined));
      } else {
        const started = await firstValueFrom(this.api.startConversation(normalized, requestId, this.selectedConnectionId() ?? undefined, this.selectedModel() ?? undefined));
        conversationId = started.conversation.id;
      }
      const ownsVisibleThread = existingConversationId
        ? this.activeConversationId() === conversationId
        : this.activeConversationId() === null && this.messages().some(item => item.client_request_id === requestId);
      if (ownsVisibleThread) {
        this.activeConversationId.set(conversationId);
        this.messages.set(await firstValueFrom(this.api.listMessages(conversationId)));
        this.setConversationUnread(conversationId, false);
      } else {
        this.setConversationUnread(conversationId, true);
      }
      this.removePendingConversation(pendingConversationId);
      await this.refreshNavigation();
      return true;
    } catch (error) {
      this.removePendingConversation(pendingConversationId);
      this.handleError(error, 'The AI assistant could not complete the response.');
      const conversationId = this.activeConversationId();
      if (conversationId) {
        try { this.messages.set(await firstValueFrom(this.api.listMessages(conversationId))); } catch { /* keep the optimistic question */ }
      }
      await this.refreshNavigation().catch(() => undefined);
      return false;
    } finally {
      this.setConversationRunning(pendingConversationId, false);
      if (existingConversationId) this.setConversationRunning(existingConversationId, false);
      this.sending.set(false);
    }
  }

  isConversationRunning(conversation: AiConversation): boolean {
    return conversation.is_running || this.runningConversationIds().has(conversation.id);
  }

  isConversationUnread(conversation: AiConversation): boolean {
    return this.unreadConversationIds().has(conversation.id);
  }

  async loadMoreRecents(): Promise<void> {
    if (!this.hasMoreRecent() || this.loadingMoreRecent()) return;
    this.loadingMoreRecent.set(true);
    try {
      const next = this.recentPage + 1;
      const page = await firstValueFrom(this.api.listConversations('recent', next));
      this.recentPage = next;
      this.hasMoreRecent.set(page.has_more);
      this.recentConversations.update(items => [...items, ...page.items.filter(item => !items.some(existing => existing.id === item.id))]);
    } finally { this.loadingMoreRecent.set(false); }
  }

  async renameConversation(conversation: AiConversation, title: string): Promise<void> {
    const updated = await firstValueFrom(this.api.updateConversation(conversation.id, { title }));
    this.replaceConversation(updated);
  }

  async togglePinned(conversation: AiConversation): Promise<void> {
    await firstValueFrom(this.api.updateConversation(conversation.id, { pinned: !conversation.pinned_at }));
    await this.refreshNavigation();
  }

  async moveToGroup(conversation: AiConversation, groupId: string | null): Promise<void> {
    await firstValueFrom(this.api.updateConversation(conversation.id, groupId ? { group_id: groupId } : { remove_from_group: true }));
    await this.refreshNavigation();
  }

  async deleteConversation(conversation: AiConversation): Promise<void> {
    await firstValueFrom(this.api.deleteConversation(conversation.id));
    if (this.activeConversationId() === conversation.id) this.newConversation();
    await this.refreshNavigation();
  }

  async createGroup(name: string): Promise<void> {
    await firstValueFrom(this.api.createConversationGroup(name));
    await this.refreshNavigation();
  }

  dismissSetup(): void { this.setupRequired.set(false); }

  async selectConnection(id: string): Promise<void> {
    const connection = this.availableConnections().find(item => item.id === id);
    this.selectedConnectionId.set(connection?.id ?? null);
    this.selectedModel.set(null);
    await this.loadSelectedConnectionModels();
  }

  selectModel(model: string): void { this.selectedModel.set(this.availableModels().includes(model) ? model : null); }
  selectScope(scope: AiConnectionScope): void { this.selectedScope.set(scope); const first = this.scopedConnections()[0]; this.selectedConnectionId.set(first?.id ?? null); this.selectedModel.set(null); void this.loadSelectedConnectionModels(); }
  connectionSettingsRoute(): string { return this.selectedScope() === 'personal' ? '/personal/configuration/ai-models' : '/configuration/organisation/ai-models'; }

  private async refreshNavigation(): Promise<void> {
    const [recent, pinned, groups] = await Promise.all([
      firstValueFrom(this.api.listConversations('recent', 1)),
      firstValueFrom(this.api.listConversations('pinned', 1, 50)),
      firstValueFrom(this.api.listConversationGroups()),
    ]);
    const grouped = await Promise.all(groups.map(async group => [group.id, (await firstValueFrom(this.api.listConversations('group', 1, 50, group.id))).items] as const));
    this.recentPage = 1;
    this.recentConversations.set(recent.items);
    this.hasMoreRecent.set(recent.has_more);
    this.pinnedConversations.set(pinned.items);
    this.groups.set(groups);
    this.groupConversations.set(Object.fromEntries(grouped));
  }

  private replaceConversation(updated: AiConversation): void {
    const replace = (items: AiConversation[]) => items.map(item => item.id === updated.id ? updated : item);
    this.recentConversations.update(replace);
    this.pinnedConversations.update(replace);
    this.groupConversations.update(groups => Object.fromEntries(Object.entries(groups).map(([id, items]) => [id, replace(items)])));
  }

  private addPendingConversation(id: string, title: string): void {
    const now = new Date().toISOString();
    this.recentConversations.update(items => [{
      id,
      title: title.slice(0, 160),
      organisation_id: null,
      group_id: null,
      pinned_at: null,
      is_running: true,
      pending: true,
      created_at: now,
      updated_at: now,
    }, ...items]);
  }

  private removePendingConversation(id: string): void {
    this.recentConversations.update(items => items.filter(item => item.id !== id));
  }

  private setConversationRunning(id: string, running: boolean): void {
    this.runningConversationIds.update(current => {
      const next = new Set(current);
      if (running) next.add(id); else next.delete(id);
      return next;
    });
  }

  private setConversationUnread(id: string, unread: boolean): void {
    this.unreadConversationIds.update(current => {
      const next = new Set(current);
      if (unread) next.add(id); else next.delete(id);
      return next;
    });
  }

  private optimisticUserMessage(content: string, requestId: string, conversationId: string): AiMessage {
    const now = new Date().toISOString();
    return { id: `pending-${requestId}`, conversation_id: conversationId, client_request_id: requestId, role: 'user', content, status: 'complete', model_label: null, widgets: [], queries: [], created_at: now, updated_at: now };
  }

  private setConnections(connections: AiModelConnection[]): void {
    this.availableConnections.set(connections);
    if (!this.scopedConnections().some(connection => connection.id === this.selectedConnectionId())) {
      this.selectedConnectionId.set(this.scopedConnections()[0]?.id ?? null);
      this.selectedModel.set(null);
    }
  }

  private async loadSelectedConnectionModels(): Promise<void> {
    const connectionId = this.selectedConnectionId();
    if (!connectionId) return;
    const cached = this.connectionModels()[connectionId];
    if (cached?.length) { if (!this.selectedModel() || !cached.includes(this.selectedModel()!)) this.selectedModel.set(this.preferredModel(cached)); return; }
    this.loadingModels.set(true);
    try {
      const response = await firstValueFrom(this.api.connectionModels(connectionId));
      this.connectionModels.update(current => ({ ...current, [connectionId]: response.models }));
      if (this.selectedConnectionId() === connectionId) this.selectedModel.set(this.preferredModel(response.models));
    } catch (error) { this.handleError(error, 'Could not load models from this provider connection.'); }
    finally { this.loadingModels.set(false); }
  }

  private preferredModel(models: string[]): string | null {
    const preferred = ['gpt-5.6-luna', 'gpt-5.6-terra', 'gpt-5.6-sol', 'gpt-4.1-mini'];
    return preferred.find(model => models.includes(model)) ?? models[0] ?? null;
  }

  private handleError(error: unknown, fallback: string): void {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { code?: string; message?: string; error?: { code?: string; message?: string } } | null;
      const code = body?.code ?? body?.error?.code;
      const message = body?.message ?? body?.error?.message ?? fallback;
      if (code === 'ai_model_connection_missing') this.setupRequired.set(true);
      if (this.isQuotaError(message)) { this.quotaError.set(message); this.error.set(''); } else { this.error.set(message); }
      return;
    }
    const message = error instanceof Error ? error.message : fallback;
    if (this.isQuotaError(message)) { this.quotaError.set(message); this.error.set(''); } else { this.error.set(message); }
  }

  private isQuotaError(message: string): boolean { const normalized = message.toLowerCase(); return normalized.includes('provider quota') || normalized.includes('rate limit'); }
}
